import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { FC, forwardRef, useMemo, useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import InputMask from "react-input-mask";

import { useAppSelector } from "../../redux/Store";
import { ITRFilingSelector } from "../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../FASvgIcon";

type DatePickerProps = {
	name: string;
	value: Date | string | undefined;
	onChange: (date: Date | null) => void;
	onBlur: any;
	size?: "lg" | "sm" | undefined;
	minDate?: Date | undefined;
	maxDate?: Date | undefined;
	isInvalid?: boolean;
	errorMsg?: string | undefined;
	showTimeInput?: boolean;
	placeholder?: string;
	isClearable?: boolean;
	showMonthDropdown?: boolean;
	showYearDropdown?: boolean;
	isDisabled?: boolean;
	label?: string;
	formType?: "ITR" | "GENERAL";
};

const DateTimePicker: FC<DatePickerProps> = (props: DatePickerProps) => {
	const {
		name,
		size = undefined,
		value,
		minDate,
		maxDate,
		onChange,
		onBlur,
		isInvalid,
		errorMsg,
		placeholder = "DD/MM/YYYY",
		showTimeInput,
		isClearable = false,
		showMonthDropdown = true,
		showYearDropdown = true,
		isDisabled = false,
		label = "",
		formType = "ITR"
	} = props;

	const inputRef = useRef(null);

	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const isFieldDisabled = useMemo(() => {
		if (formType === "GENERAL") {
			if (isDisabled) {
				return isDisabled;
			}
			return false;
		}
		if (isFilingReadOnly) {
			return true;
		}
		return isDisabled;
	}, [isDisabled, isFilingReadOnly, formType]);

	const RenderInput = forwardRef((_props: any, ref) => (
		<>
			<InputMask mask="99/99/9999" {..._props} value={_props.value} onChange={_props.onChange}>
				{() => (
					<InputGroup size={size} className={classNames(isFieldDisabled && "is-disabled")}>
						<Form.Control
							{..._props}
							ref={ref}
							name={name}
							type="text"
							size={size}
							placeholder={placeholder}
							onBlur={onBlur}
							isInvalid={isInvalid}
						/>
						<InputGroup.Text
							onClick={() => (!isFieldDisabled ? _props.onClick() : null)}
							className={classNames("append-text", isInvalid && "border-danger")}
						>
							<FASvgIcon icon={faCalendarAlt} />
						</InputGroup.Text>
					</InputGroup>
				)}
			</InputMask>
		</>
	));

	return (
		<>
			<Form.Group className="form-group" controlId={name}>
				{label ? <Form.Label className="small-label">{label}</Form.Label> : null}
				<DatePicker
					selected={value ? new Date(value) : undefined}
					minDate={minDate}
					maxDate={maxDate}
					customInput={<RenderInput ref={inputRef} />}
					onChange={(date) => onChange(date)}
					isClearable={isClearable}
					clearButtonClassName="eds-clear"
					shouldCloseOnSelect={true}
					showTimeSelect={showTimeInput}
					timeFormat="HH:mm"
					timeIntervals={15}
					timeCaption="time"
					dateFormat={showTimeInput ? "dd/MM/yyyy, h:mm aa" : "dd/MM/yyyy"}
					showMonthDropdown={showMonthDropdown}
					showYearDropdown={showYearDropdown}
					dropdownMode="select"
					calendarClassName="eds-calendar"
					disabled={isFieldDisabled}
					toggleCalendarOnIconClick={true}
				/>
				{isInvalid && errorMsg ? <div className="field-error">{errorMsg}</div> : null}
			</Form.Group>
		</>
	);
};

export default DateTimePicker;
